import React, { useState, useEffect } from 'react';
import { Movie, MovieFormData, MovieStatus } from '../../types/Movie';
import './MovieForm.css';

interface MovieFormProps {
  movie?: Movie;
  onSubmit: (movie: Movie) => void;
  onCancel: () => void;
}

const MovieForm: React.FC<MovieFormProps> = ({ movie, onSubmit, onCancel }) => {
  const [formData, setFormData] = useState<MovieFormData>({
    name: '',
    director: '',
    year: undefined,
    genre: '',
    rating: undefined,
    duration: undefined,
    status: undefined,
    posterUrl: '',
    notes: ''
  });
  
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    if (movie) {
      setFormData({
        name: movie.name,
        director: movie.director || '',
        year: movie.year,
        genre: movie.genre || '',
        rating: movie.rating,
        duration: movie.duration,
        status: movie.status,
        posterUrl: movie.posterUrl || '',
        notes: movie.notes || ''
      });
    }
  }, [movie]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      alert('Name is required!');
      return;
    }

    if (isLoading) {
      return; // Prevent multiple submissions
    }

    setIsLoading(true);

    try {
      const movieData: Movie = {
        id: movie?.id || '',
        name: formData.name.trim(),
        director: formData.director?.trim() || undefined,
        year: formData.year,
        genre: formData.genre?.trim() || undefined,
        rating: formData.rating,
        duration: formData.duration,
        status: formData.status,
        posterUrl: formData.posterUrl?.trim() || undefined,
        notes: formData.notes?.trim() || undefined,
        tags: [],
        dateAdded: movie?.dateAdded || new Date().toISOString()
      };

      await onSubmit(movieData);
    } catch (error) {
      console.error('Error submitting movie:', error);
      alert('An error occurred while saving the movie. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'rating' || name === 'year' || name === 'duration'
        ? (value ? parseInt(value) : undefined)
        : value
    }));
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <h2>{movie ? 'Edit Movie' : 'Add New Movie'}</h2>
          <button className="close-btn" onClick={onCancel}>×</button>
        </div>
        
        <form onSubmit={handleSubmit} className="movie-form">
          {isLoading && (
            <div className="loading-overlay">
              <div className="loading-spinner"></div>
              <p className="loading-message">Saving...</p>
            </div>
          )}
          
          <div className="form-group">
            <label htmlFor="name">Name *</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              disabled={isLoading}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="director">Director</label>
            <input
              type="text"
              id="director"
              name="director"
              value={formData.director}
              onChange={handleChange}
              disabled={isLoading}
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="year">Year</label>
              <input
                type="number"
                id="year"
                name="year"
                value={formData.year || ''}
                onChange={handleChange}
                disabled={isLoading}
                min="1800"
                max="2100"
              />
            </div>

            <div className="form-group">
              <label htmlFor="genre">Genre</label>
              <input
                type="text"
                id="genre"
                name="genre"
                value={formData.genre}
                onChange={handleChange}
                disabled={isLoading}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="rating">Rating</label>
              <select
                id="rating"
                name="rating"
                value={formData.rating || ''}
                onChange={handleChange}
                disabled={isLoading}
              >
                <option value="">No Rating</option>
                <option value="1">1 Star</option>
                <option value="2">2 Stars</option>
                <option value="3">3 Stars</option>
                <option value="4">4 Stars</option>
                <option value="5">5 Stars</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="duration">Duration (minutes)</label>
              <input
                type="number"
                id="duration"
                name="duration"
                value={formData.duration || ''}
                onChange={handleChange}
                disabled={isLoading}
                min="1"
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="status">Status</label>
            <select
              id="status"
              name="status"
              value={formData.status || ''}
              onChange={handleChange}
              disabled={isLoading}
            >
              <option value="">No Status</option>
              <option value={MovieStatus.WANT_TO_WATCH}>Want to Watch</option>
              <option value={MovieStatus.WATCHING}>Watching</option>
              <option value={MovieStatus.COMPLETED}>Completed</option>
              <option value={MovieStatus.PAUSED}>Paused</option>
              <option value={MovieStatus.ABANDONED}>Abandoned</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="posterUrl">Poster URL</label>
            <input
              type="url"
              id="posterUrl"
              name="posterUrl"
              value={formData.posterUrl}
              onChange={handleChange}
              placeholder="https://example.com/poster.jpg"
              disabled={isLoading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="notes">Notes</label>
            <textarea
              id="notes"
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              rows={3}
              disabled={isLoading}
            />
          </div>

          <div className="form-actions">
            <button type="button" className="btn-cancel" onClick={onCancel} disabled={isLoading}>
              Cancel
            </button>
            <button type="submit" className="btn-submit" disabled={isLoading}>
              {isLoading ? (
                <>
                  <span className="button-spinner"></span>
                  {movie ? 'Updating...' : 'Adding...'}
                </>
              ) : (
                movie ? 'Update Movie' : 'Add Movie'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default MovieForm;

